import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Flex,
  Popover,
  Rating,
  Space,
  Text,
  Collapse,
  Textarea,
  Checkbox
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { DueRatingItem } from '@shared/api/requests/artist/rating/get';
import { useSubmitRatingMutation } from '@shared/api/requests/artist/rating/post';
import { translate } from '@shared/i18n/translate.tsx';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { LBButton } from '@shared/ui-kit/components';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

interface RatingProps {
  averageRating: number; // e.g., 3.7
  ratingsCount: number; // e.g., 3.7
}

export const RatingStars = ({ averageRating, ratingsCount }: RatingProps) => (
  <Flex align='center' gap={6}>
    <Text c='#f5a623' fw='bold'>
      {averageRating.toFixed(1)}
    </Text>
    <Flex gap={1}>
      {Array.from({ length: 5 }, (_, i) => {
        // Determine the fill % for each star
        const fill = Math.min(Math.max(averageRating - i, 0), 1) * 100;

        return (
          <span
            key={i}
            style={{
              position: 'relative',
              display: 'inline-block',
              color: '#ccc', // gray background
              fontSize: 14,
              lineHeight: 1
            }}
          >
            ★
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: `${fill}%`,
                overflow: 'hidden',
                color: '#f5a623'
              }}
            >
              ★
            </span>
          </span>
        );
      })}
    </Flex>
    <Text c='gray'>({ratingsCount})</Text>
  </Flex>
);

type ClubRatingProps = {
  averageRating: number;
  ratingAverage?: {
    totalAvg: number;
    housing: number;
    dressingRoom: number;
    staffAndManagement: number;
    ratingsCount: number;
  } | null;
  clubProfileId: number;
  variant?: 'dropdown' | 'inline';
  mode?: 'artist' | 'club';
  dueItems?: DueRatingItem[];
};

export const ClubRating = ({
  averageRating,
  ratingAverage,
  clubProfileId,
  variant = 'dropdown',
  mode,
  dueItems = []
}: ClubRatingProps) => {
  const [opened, setOpened] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [housing, setHousing] = useState(0);
  const [dressingRoom, setDressingRoom] = useState(0);
  const [staff, setStaff] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const isClub = mode === 'club';
  const [feedback, setFeedback] = useState('');
  const [anonymous, setAnonymous] = useState(false);

  const artistId = useProfileStore((state) => state.profile?.profileIds[0]);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (mode !== 'artist') return;
    const currentCanRate = useProfileStore.getState().canRate;
    const newCanRate = dueItems?.length > 0;

    if (currentCanRate !== newCanRate) {
      useProfileStore.getState().setCanRate(newCanRate);
    }
  }, [dueItems]);

  const canRate =
    mode === 'artist'
      ? dueItems?.some(
          (item) =>
            item?.clubProfileId === clubProfileId &&
            item.status === 'ContractCompleted' &&
            !item.rated
        )
      : false;

  const displayRating = ratingAverage ?? {
    housing: 0,
    dressingRoom: 0,
    staffAndManagement: 0,
    ratingsCount: 0
  };

  const submitRatingMutation = useSubmitRatingMutation({
    options: {
      onSuccess: () => {
        if (artistId)
          queryClient.invalidateQueries({ queryKey: ['RatingsDue', artistId] });
        if (clubProfileId) {
          queryClient.invalidateQueries({
            queryKey: ['showFullClubProfile', clubProfileId]
          });
        }
        // Закрываем форму
        setExpanded(false);
        setOpened(false);

        // Показ уведомления
        showNotification({
          message: translate('commons.success_send_data'),
          color: 'green'
        });
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to submit rating',
          color: 'red'
        });
      }
    }
  });

  const isSubmitDisabled = !(housing && dressingRoom && staff);

  const handleSubmit = async () => {
    if (!artistId || !clubProfileId) return;
    if (mode !== 'artist') return;

    // Находим текущий dueItem, связанный с этим клубом
    const current = dueItems.find(
      (item) =>
        item.clubProfileId === clubProfileId &&
        item.status === 'ContractCompleted' &&
        !item.rated
    );

    if (!current) {
      return;
    }

    const idKey =
      current.workItemType === 'Invite' ? 'workInviteId' : 'workRequestId';

    const payload = {
      artistProfileId: artistId,
      [idKey]: current.workItemId,
      clubProfileId: current.clubProfileId,
      StaffAndManagement: staff,
      DressingRoom: dressingRoom,
      Housing: housing,
      Comment: feedback,
      Anonymous: anonymous
    };

    setIsSubmitting(true);
    try {
      await submitRatingMutation.mutateAsync({
        params: {
          artistProfileId: artistId.toString(),
          payload
        }
      });
      setExpanded(false);
      setOpened(false);
    } catch (e) {
      // Ошибка уже залогирована в onError мутации
    } finally {
      setIsSubmitting(false);
    }
  };
  const hasRating = displayRating && displayRating.ratingsCount > 0;

  /** reusable block for rating categories */
  const RatingDetails = (
    <Flex direction='column' gap={10}>
      {(averageRating === null || averageRating === 0) && (
        <Text
          fz={13}
          mb={10}
          style={{ textAlign: 'center', color: 'lightslategray' }}
        >
          {translate('commons.no_rating_yet')}
        </Text>
      )}
      <Flex align='center' justify='space-between'>
        <Text fz={13}>{translate('commons.housing_quality')}</Text>
        <Flex gap={2}>
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              style={{ color: i < displayRating?.housing ? '#f5a623' : '#ccc' }}
            >
              ★
            </span>
          ))}
        </Flex>
      </Flex>

      <Flex align='center' justify='space-between'>
        <Text fz={13}>{translate('commons.dressing_quality')}</Text>
        <Flex gap={2}>
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              style={{
                color: i < displayRating?.dressingRoom ? '#f5a623' : '#ccc'
              }}
            >
              ★
            </span>
          ))}
        </Flex>
      </Flex>

      <Flex align='center' justify='space-between'>
        <Text fz={13}>{translate('commons.management')}</Text>
        <Flex gap={2}>
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              style={{
                color:
                  i < displayRating?.staffAndManagement ? '#f5a623' : '#ccc'
              }}
            >
              ★
            </span>
          ))}
        </Flex>
      </Flex>

      {variant === 'dropdown' && (
        <Text fz={13} style={{ textAlign: 'center', color: '#f62144' }}>
          {translate('commons.rating_allow')}
        </Text>
      )}

      {!isClub && canRate && (
        <Text
          fz={13}
          mt={12}
          style={{
            color: '#0077cc',
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: 'underline'
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {translate('commons.rate_work')}
        </Text>
      )}

      {/* rating form */}
      <Collapse in={expanded}>
        <Space h={12} />
        <Flex direction='column' gap={12}>
          <Box
            role='presentation'
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <Text fw={500} fz={13}>
              {translate('commons.housing_quality')}
            </Text>
            <Rating value={housing} onChange={setHousing} />
          </Box>
          <Box
            role='presentation'
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <Text fw={500} fz={13}>
              {translate('commons.dressing_quality')}
            </Text>
            <Rating value={dressingRoom} onChange={setDressingRoom} />
          </Box>
          <Box
            role='presentation'
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <Text fw={500} fz={13}>
              {translate('commons.management')}
            </Text>
            <Rating value={staff} onChange={setStaff} />
          </Box>

          {/* Новое поле комментария */}
          <Textarea
            autosize
            maxRows={6}
            minRows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.currentTarget.value)}
            onClick={(e) => e.stopPropagation()} // чтобы попап не закрывался
          />

          {/* Чекбокс анонимно */}
          <Checkbox
            checked={anonymous}
            label={translate('commons.anonymous')}
            onChange={(e) => setAnonymous(e.currentTarget.checked)}
            onClick={(e) => e.stopPropagation()} // чтобы попап не закрывался
          />

          <Flex justify='space-between' mt={10}>
            <LBButton
              size='xs'
              style={{ fontSize: '12px', height: '40px' }}
              variant='light'
              onClick={() => setExpanded(false)}
            >
              {translate('commons.cancel')}
            </LBButton>
            <LBButton
              disabled={isSubmitDisabled || isSubmitting}
              loading={isSubmitting}
              size='xs'
              style={{ fontSize: '12px', height: '40px' }}
              onClick={handleSubmit}
            >
              {translate('commons.submit')}
            </LBButton>
          </Flex>
        </Flex>
      </Collapse>
    </Flex>
  );

  /** RENDERING VARIANTS */
  if (variant === 'inline') {
    return (
      <Flex direction='column' gap={8}>
        <RatingStars
          averageRating={averageRating}
          ratingsCount={displayRating.ratingsCount}
        />
        {RatingDetails}
      </Flex>
    );
  }

  console.log();

  return (
    <Popover
      opened={opened}
      position='bottom-start'
      shadow='md'
      width={280}
      onChange={setOpened}
    >
      <Popover.Target>
        <Flex
          align='center'
          justify='space-between' // main row: stars left, icon right
          mt={6}
          style={{ width: '100%' }}
        >
          <Box
            style={{ cursor: 'pointer', display: 'flex', gap: 4 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpened((o) => !o);
            }}
          >
            {/* Left side: rating stars */}
            <RatingStars
              averageRating={averageRating}
              ratingsCount={displayRating?.ratingsCount || 0}
            />
          </Box>
          {hasRating && (
            <Text
              c='gray'
              fz='13px'
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                if (!artistId) return;

                // сохраняем id клуба и текущую позицию
                sessionStorage.setItem(
                  'searchClubId',
                  clubProfileId.toString()
                );
                sessionStorage.setItem(
                  'searchScrollY',
                  window.scrollY.toString()
                );

                navigate({
                  to: '/artist/$id/profile-info/$clubId/reviews',
                  params: {
                    id: artistId.toString(),
                    clubId: clubProfileId.toString()
                  }
                });
              }}
            >
              {translate('commons.reviews')}
            </Text>
          )}

          <Box
            style={{ cursor: 'pointer', display: 'flex', gap: 4 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpened((o) => !o);
            }}
          >
            {/* Right side: dropdown icon */}
            <ChevronDownIcon
              style={{
                width: 16,
                height: 16,
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: '#666'
              }}
            />
          </Box>
        </Flex>
      </Popover.Target>

      <Popover.Dropdown
        p={12}
        style={{ backgroundColor: '#f9f9f9', borderRadius: 8 }}
      >
        {RatingDetails}
      </Popover.Dropdown>
    </Popover>
  );
};
